// The boss contract (client side). Each boss has one module here exporting a BossClientFactory,
// plus one line in ./index.ts. It owns everything the client does for that boss: its model and
// telegraphs, its predicted hit volumes (aim label + local hit marker), its boss-bar fill, its line
// in the objective plate and its sounds. client/game.ts only routes. See sim/bosses/README.md.

import type * as THREE from 'three';
import type { BossKind, BossNet } from '../../../../sim/bosses/types';
import type { NetEnemy } from '../../../../sim/protocol';
import type { Sfx } from '../../../audio/sfx';
import type { Fx } from '../../fx/index';
import type { HudBoss, HudObjective } from '../../contracts';

/** What a boss view gets each frame. */
export interface BossFrame {
  net: BossNet;
  /** this boss's bodies in the snapshot (interpolated, like robots): units[0..n) */
  units: NetEnemy[]; n: number;
  /** the body whose id is net.eid, if any */
  primary: NetEnemy | null;
  /** seconds since a body was last hit (99 = not lately) */
  hitAge(id: number): number;
  dt: number; time: number;
  ground(x: number, z: number): number;
}

/** What a boss view may poke in the client. */
export interface BossCtx {
  scene: THREE.Scene;
  time: { value: number };
  sfx: Sfx;
  fx(): Fx | null;
  shake(amount: number): void;
  rumble(lo: number, hi: number, ms: number): void;
  flash(amount: number): void;
  chroma(amount: number): void;
  /** compile, upload and draw these off screen in the frame budget (render/warm.ts) so the first sight of the boss doesn't hitch; call once its model is built */
  prewarm?(objs: THREE.Object3D[]): void;
}

/** A predicted hit: kind is the hit-marker kind (2 body, 3 crit, 4 clang/armour); label names the part. */
export interface BossAim { t: number; kind: number; head: boolean; label: string }
export const newBossAim = (): BossAim => ({ t: -1, kind: 0, head: false, label: '' });

export interface BossClient {
  readonly kind: BossKind;
  /** the intro card: BossIntro's objective title + detail */
  readonly title: string; readonly sub: string;
  /** every frame once created; f = null: no boss this frame (hold the last pose a beat, play a death, then hide) */
  update(f: BossFrame | null, time: number, dt: number): void;
  /** one of its bodies died (Ev.EnemyDie) */
  bodyDied(type: number, x: number, y: number, z: number, time: number): void;
  /** one of its Ev.Boss beats ([eid, kind, a, b]) */
  onEvent(kind: number, a: number, b: number, eid: number, f: BossFrame | null, time: number): void;
  /** client-side ray (same volumes as the server) for the aim label and the local hit marker */
  ray(f: BossFrame, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, ap: number, out: BossAim): BossAim;
  /** fill the boss bar; null hides it */
  hud(f: BossFrame, aimLabel: string, out: HudBoss): HudBoss | null;
  /** the objective plate during the fight (title/detail/tone already reset by the caller) */
  objective(f: BossFrame | null, o: HudObjective): void;
  dispose(): void;
}

export type BossClientFactory = (ctx: BossCtx) => BossClient;

export const newHudBoss = (): HudBoss => ({ name: '', sub: '', hp: 1, pips: [], count: '', hyper: false, meter: '', crack: 0, note: '', warn: '', aimPart: '' });
